import React from "react";
import {IProject} from "../../models/interfaces/project.interface.ts";
import {Tab, TabIndicator, TabList, TabPanels, Tabs} from "@chakra-ui/react";
import SubtitlesTranscreationFeatureProgress from "./SubtitlesTranscreationFeatureProgress.tsx";
import SingleFeatureProgress from "./singleFeatureProgress.tsx";
import AudioDescriptionFeatureProgress from "./AudioDescriptionFeatureProgress.tsx";
import {camelCaseToTitleCase} from "../../utils/utils.ts";

interface ProjectDrawerFeaturesProgressProps {
  project: IProject;
}

/**
 * A feature that owns BOTH its tab and the panel behind it.
 *
 * Chakra pairs a tab to its panel by POSITIONAL INDEX, never by identity, so
 * the tab list and the panel list must come from one already-filtered array.
 * Filtering them separately - as this component used to - shifted every panel
 * after a missing feature, and a legacy project without `dialogueList` rendered
 * the wrong feature (or nothing) when a later tab was clicked.
 */
interface ProjectFeatureTab {
  key: string;
  tab: React.ReactNode;
  panel: React.ReactElement;
}

interface ProjectFeatureTabCandidate {
  key: string;
  exists: boolean;
  panel: React.ReactElement;
}

const ProjectDrawerFeaturesProgress: React.FC<ProjectDrawerFeaturesProgressProps> = ({project}: ProjectDrawerFeaturesProgressProps) => {
  const featureCandidates: ProjectFeatureTabCandidate[] = [
    {
      key: "subtitlesTranscreation",
      exists: true,
      panel: <SubtitlesTranscreationFeatureProgress project={project}/>,
    },
    {
      key: "dialogueList",
      exists: !!project.featuresStates?.dialogueList,
      panel: (
        <SingleFeatureProgress
          projectId={project.id}
          projectName={project.name}
          feature={project.featuresStates.dialogueList}
          featureName={"dialogueList"}
        />
      ),
    },
    {
      key: "extendedMetadata",
      exists: !!project.featuresStates?.extendedMetadata,
      panel: (
        <SingleFeatureProgress
          projectId={project.id}
          projectName={project.name}
          feature={project.featuresStates.extendedMetadata}
          featureName={"extendedMetadata"}
        />
      ),
    },
    {
      key: "audioDescriptions",
      exists: !!project.featuresStates?.audioDescriptions,
      panel: <AudioDescriptionFeatureProgress project={project}/>,
    },
  ];

  const activeFeatures: ProjectFeatureTab[] = featureCandidates
    .filter((candidate: ProjectFeatureTabCandidate) => candidate.exists)
    .map((candidate: ProjectFeatureTabCandidate) => ({
      key: candidate.key,
      tab: camelCaseToTitleCase(candidate.key),
      panel: candidate.panel,
    }));

  return (
    <div className="flex flex-col mt-6 ">
      <Tabs position='relative' variant="unstyled">
        <TabList>
          {activeFeatures.map((feature: ProjectFeatureTab) => (
            <Tab key={feature.key}>{feature.tab}</Tab>
          ))}
        </TabList>
        <div className="w-full bg-n-500 h-[1px]"/>
        <TabIndicator mt='-1.5px' height='2px' bg='brandPurple.500' borderRadius='1px' zIndex={10}/>
        <TabPanels>
          {/*
            Each feature component renders its OWN `TabPanel`, so the element is
            handed to `TabPanels` directly - wrapping it again would nest two
            panels under one index. `cloneElement` only attaches the list key.
          */}
          {activeFeatures.map((feature: ProjectFeatureTab) => React.cloneElement(feature.panel, {key: feature.key}))}
        </TabPanels>
      </Tabs>
    </div>
  )
}

export default ProjectDrawerFeaturesProgress;
